import React, { useEffect, useMemo, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Overlay } from '~/ui/overlay/Overlay';
import { Modal } from '~/ui/modal/Modal';
import CloseIcon from '~/assets/icons/close.svg';
import { EditBtnIcon } from '~/editor/buttons/EditBtn';
import FilePickerV2 from '~/editor/filePicker/FilePicker_v2';
import emptyImg from '~/training/sequence/templates/items/doorCard/empty.png';
import { useEditorStore, EditorMediaFile } from '~/shell/Store';
import { stripHtml, sanitizeBodyForSave } from '~/lib/sanitization';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';

const StyledModal = styled(Modal)`
  border-radius: 24px !important;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px; /* reduced spacing */
  @media (max-height: 900px) {
    margin-bottom: 10px;
  }
  @media (max-height: 780px) {
    margin-bottom: 8px;
  }
`;
const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    opacity: 0.7;
  }
`;
const StyledCloseIcon = styled(CloseIcon)`
  width: 20px;
  height: 20px;
`;
const Title = styled.h3`
  margin: 0;
  font-family: Raleway;
  font-size: 18px;
  font-weight: 600;
  text-align: left;
  @media (max-height: 900px) {
    font-size: 17px;
  }
  @media (max-height: 780px) {
    font-size: 16px;
  }
`;
const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-family: Raleway;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  @media (max-height: 900px) {
    font-size: 13px;
    margin-bottom: 6px;
  }
  @media (max-height: 780px) {
    font-size: 12.5px;
    margin-bottom: 4px;
  }
`;
const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-family: Raleway;
  font-size: 14px;
  margin-bottom: 16px;
  background: #f8f9fa;
  &:focus {
    outline: none;
    border-color: #9738ff;
    background: white;
  }
  &::placeholder {
    color: #999;
  }
  @media (max-height: 900px) {
    padding: 10px 14px;
    font-size: 13px;
    margin-bottom: 14px;
  }
  @media (max-height: 780px) {
    padding: 8px 12px;
    font-size: 12.5px;
    margin-bottom: 12px;
  }
`;
const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-family: Raleway;
  font-size: 14px;
  margin-bottom: 16px;
  background: #f8f9fa;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #9738ff;
    background: white;
  }
  &::placeholder {
    color: #999;
  }
  @media (max-height: 900px) {
    min-height: 110px;
    padding: 10px 14px;
    font-size: 13px;
    margin-bottom: 14px;
  }
  @media (max-height: 780px) {
    min-height: 100px;
    padding: 8px 12px;
    font-size: 12.5px;
    margin-bottom: 12px;
  }
`;
const ImageBox = styled.div`
  width: 70%;
  height: 180px; /* taller preview */
  border: 2px dashed #d4d8dd;
  border-radius: 12px;
  background: #fafbfc;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  margin-left: auto;
  margin-right: auto;
`;
const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
const EditImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  margin: 0;
  padding: 5px;
  background-color: #f3ecff;
  color: #9738ff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color 0.18s ease,
    color 0.18s ease;

  svg {
    width: 18px;
    height: 18px;
    display: block;
  }
  svg path {
    fill: currentColor !important;
    transition: fill 0.18s ease;
  }

  &:hover {
    background-color: #9738ff;
    color: #fff;
    svg path {
      fill: #fff !important;
    }
  }

  &.active {
    background-color: #9845fb;
    color: #fff;
    svg path {
      fill: #fff !important;
    }
  }
`;
const ButtonRow = styled.div`
  display: flex;
`;
const SubmitButton = styled.button`
  width: 100%;
  padding: 10px 20px;
  border-radius: 10px;
  font-family: Raleway;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: #9738ff;
  color: white;
  letter-spacing: 0.3px;
  transition: background 0.15s ease;
  &:hover {
    background: #7a2dcc;
  }
  &:active {
    background: #5d22a3;
  }
  @media (max-height: 900px) {
    padding: 9px 18px;
    font-size: 14.5px;
  }
  @media (max-height: 780px) {
    padding: 8px 16px;
    font-size: 14px;
  }
`;

interface FlipCardEditModalProps {
  open: boolean;
  onClose: () => void;
  cardId: string | number;
  isNew?: boolean;
  defaultImageUrl?: string;
  separateTitleAndBody?: boolean;
  showSubtitle?: boolean;
  showTitle?: boolean;
}

const FlipCardEditModal: React.FC<FlipCardEditModalProps> = ({
  open,
  onClose,
  cardId,
  isNew = false,
  defaultImageUrl,
  separateTitleAndBody = false,
  showSubtitle = false,
  showTitle = false
}) => {
  const { sequence, updateSequence, updateFile, contentEditable, addSubItem } =
    useEditorStore();
  const [title, setTitle] = useState('');
  const [cardTitle, setCardTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState<string | undefined>();

  interface ImageFile extends EditorMediaFile {
    url?: string;
  }

  interface CardLike {
    body?: string;
    title?: string;
    subtitle?: string;
    values?: { body?: string };
    value?: string;
    __typename?: string;
  }

  const [selectedImageFile, setSelectedImageFile] = useState<
    ImageFile | undefined
  >();

  const [showFilePicker, setShowFilePicker] = useState(false);
  const initialImageIdRef = useRef<string | number | undefined>(undefined);

  // only allow when content is editable
  if (!contentEditable) return null;

  const cardItem = useMemo(() => {
    if (!sequence?.items) return undefined;
    const stack = [...sequence.items];
    while (stack.length) {
      const it = stack.shift();
      if (!it) break;
      if (it.id?.toString() === cardId.toString()) return it;
      if (it.items) stack.push(...it.items);
    }
    return undefined;
  }, [sequence, cardId]);

  // Helper to strip HTML tags for editing plain text, preserving line breaks
  // We explicitly convert <br> tags to placeholders before decoding so that
  // sequential <br><br> become two newline characters (representing a blank line).
  useEffect(() => {
    if (isNew) {
      setTitle('');
      setCardTitle('');
      setSubtitle('');
      setBody('');
      if (defaultImageUrl) {
        setImageUrl(defaultImageUrl);
      } else {
        setImageUrl(emptyImg as unknown as string);
      }
      initialImageIdRef.current = undefined;
      return;
    }

    if (cardItem) {
      // Always derive title from bolded segment at start of body (if present), not from the card's title field
      const rawBodyHtml =
        (cardItem as CardLike).body ||
        (cardItem.values as { body?: string } | undefined)?.body ||
        (cardItem as CardLike).value ||
        '';

      const firstBoldPattern = /<b>(.*?)<\/b>/i; // first bold occurrence anywhere
      let extractedTitle = '';
      let workingBodyHtml = rawBodyHtml;
      const match = rawBodyHtml.match(firstBoldPattern);
      if (match) {
        extractedTitle = stripHtml(match[1]);
        const removePattern = new RegExp(
          `<b>${match[1].replace(
            /[-/\\^$*+?.()|[\]{}]/g,
            (r: string) => '\\' + r
          )}<\\/b>(?:<br\\s*\\/?><br\\s*\\/?>)?`,
          'i'
        );
        workingBodyHtml = workingBodyHtml.replace(removePattern, '');
      }
      setTitle(
        separateTitleAndBody
          ? (cardItem as CardLike)?.title || ''
          : extractedTitle
      );
      setSubtitle((cardItem as CardLike)?.subtitle || '');
      if (!separateTitleAndBody && showTitle) {
        setCardTitle((cardItem as CardLike)?.title || '');
      }

      setBody(stripHtml(workingBodyHtml).replace(/^\n+/, ''));

      if (cardItem.pictures && cardItem.pictures[0]) {
        setImageUrl(cardItem.pictures[0].mediaUrl);
        initialImageIdRef.current = cardItem.pictures[0].id;
      } else {
        setImageUrl(emptyImg as unknown as string);
        initialImageIdRef.current = undefined;
      }
    }
  }, [cardItem, isNew, defaultImageUrl, separateTitleAndBody, showTitle]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showFilePicker) {
          setShowFilePicker(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showFilePicker, onClose]);

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (separateTitleAndBody) {
      if (!title.trim() && !body.trim()) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'Title or Description is required'
        });
        return;
      }
    }

    const escapeHtml = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const sanitizedBodyContent = sanitizeBodyForSave(body); // preserve internal/leading newlines (leading ones already stripped for display)
    const escapedTitle = escapeHtml(title.trim());

    let combinedBodyHtml = '';

    if (escapedTitle.trim()) {
      combinedBodyHtml =
        `<b>${escapedTitle}</b>` +
        (sanitizedBodyContent ? `<br><br>${sanitizedBodyContent}` : '');
    } else {
      combinedBodyHtml = sanitizedBodyContent ? `${sanitizedBodyContent}` : '';
    }

    if (isNew) {
      if (!sequence) return onClose();
      const cardTitleValue = separateTitleAndBody
        ? title
        : showTitle
          ? cardTitle
          : title;
      const position =
        (sequence.items?.filter(it => it.__typename === 'Card').length || 0) +
        1;
      const tempId = cardId;
      addSubItem({
        sequenceId: sequence.id,
        data: {
          type: 'CARD',
          title: cardTitleValue,
          value: cardTitleValue,
          subtitle: subtitle.trim() || undefined,
          body: separateTitleAndBody ? sanitizedBodyContent : combinedBodyHtml,
          position,
          parent_id: null
        },
        newItem: {
          id: tempId,
          position,
          title: cardTitleValue,
          subtitle: subtitle.trim() || undefined,
          body: separateTitleAndBody ? sanitizedBodyContent : combinedBodyHtml,
          correct: false,
          pictures:
            selectedImageFile && selectedImageFile.id ? [selectedImageFile] : []
        },
        persistFile: !!(selectedImageFile && selectedImageFile.id),
        imageFile:
          selectedImageFile && selectedImageFile.id
            ? (selectedImageFile as { id: string | number })
            : undefined
      });
    } else if (cardItem) {
      if (separateTitleAndBody) {
        updateSequence(cardId, 'body', sanitizedBodyContent);
        updateSequence(cardId, 'title', title);
        updateSequence(cardId, 'subtitle', subtitle.trim());
      } else {
        updateSequence(cardId, 'body', combinedBodyHtml);
        updateSequence(
          cardId,
          'title',
          showTitle ? cardTitle.trim() : title.trim()
        );
        updateSequence(cardId, 'subtitle', subtitle.trim());
      }

      if (
        selectedImageFile?.id &&
        selectedImageFile.id !== initialImageIdRef.current
      ) {
        updateFile(cardId.toString(), selectedImageFile!, 'CARD');
      }
    }
    onClose();
  };

  const fileElRef = useRef<HTMLDivElement>(null);

  const handleImageSelect = (file: ImageFile) => {
    setSelectedImageFile(file);
    setImageUrl(file.mediaUrl || file.url);
    setShowFilePicker(false);
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <Overlay open={open} opacity={0.5} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ position: 'relative', zIndex: 9999 }}
      >
        <StyledModal
          open
          width="520px"
          horizontal="center"
          vertical="center"
          padding="20px 24px 24px"
          minHeight="550px"
        >
          <div style={{ maxHeight: '100vh', overflowY: 'auto' }}>
            <ModalHeader>
              <Title>{isNew ? 'New Card' : 'Edit Card'}</Title>
              <CloseButton onClick={onClose} aria-label="Close edit card modal">
                <StyledCloseIcon />
              </CloseButton>
            </ModalHeader>
            <form onSubmit={handleSubmit}>
              <Label>Card Image</Label>
              <ImageBox ref={fileElRef}>
                <ImagePreview
                  src={imageUrl || (emptyImg as unknown as string)}
                  alt="Card image"
                />
                <EditImageButton
                  type="button"
                  aria-label="Edit image"
                  onClick={() => setShowFilePicker(true)}
                >
                  <EditBtnIcon />
                </EditImageButton>
              </ImageBox>

              {!separateTitleAndBody && showTitle && (
                <>
                  <Label htmlFor="flip-card-card-title">Card Title</Label>
                  <Input
                    id="flip-card-card-title"
                    value={cardTitle}
                    maxLength={120}
                    onChange={e => setCardTitle(e.target.value)}
                    placeholder="Enter card title (e.g. author name)"
                  />
                </>
              )}

              {showSubtitle && (
                <>
                  <Label htmlFor="flip-card-subtitle">Card Subtitle</Label>
                  <Input
                    id="flip-card-subtitle"
                    value={subtitle}
                    maxLength={120}
                    onChange={e => setSubtitle(e.target.value)}
                    placeholder="Enter subtitle (e.g. @username)"
                  />
                </>
              )}

              <Label htmlFor="flip-card-title">
                {separateTitleAndBody ? 'Card Title' : 'Title (in body)'}
              </Label>
              <Input
                id="flip-card-title"
                value={title}
                maxLength={120}
                onChange={e => setTitle(e.target.value)}
                placeholder="Enter title"
              />

              <Label htmlFor="flip-card-body">Card Description</Label>
              <TextArea
                id="flip-card-body"
                value={body}
                maxLength={1000}
                onChange={e => setBody(e.target.value)}
                placeholder="Enter description"
              />

              <ButtonRow>
                <SubmitButton type="submit" aria-label={'Submit'}>
                  {'Submit'}
                </SubmitButton>
              </ButtonRow>
            </form>
          </div>
        </StyledModal>
        {showFilePicker &&
          ReactDOM.createPortal(
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 30000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {/* backdrop */}
              <div
                onClick={() => setShowFilePicker(false)}
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(0,0,0,0.35)'
                }}
              />
              <div
                style={{
                  position: 'relative',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  zIndex: 30001,
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                  overflow: 'hidden'
                }}
              >
                <FilePickerV2
                  updateFile={handleImageSelect}
                  type="image"
                  fileElRef={fileElRef}
                  imageGenerationSlot={{
                    placement: 'card_image',
                    itemId: cardId,
                    slotKey: 'pictures'
                  }}
                  onClose={() => setShowFilePicker(false)}
                />
              </div>
            </div>,
            document.body
          )}
      </div>
    </Overlay>,
    document.body
  );
};

export default FlipCardEditModal;
